import * as httpx from "httpx"
import * as uuid from "uuid"
import * as events from "./events"
import { CancellablePromise } from "real-cancellable-promise"
import {
	DocumentSearchRequest,
	DocumentSearchResponse,
	DocumentFindResponse,
	DocumentPatchRequest,
	DocumentPatchResponse,
	DocumentUploadResponse,
	DocumentVersionHistoryResponse,
} from "brandguard/brandguard.document_pb"

function upload(file: File, bid: string, ancestorId: string | undefined, ...options: httpx.option[]): CancellablePromise<DocumentUploadResponse> {
	const data = new FormData()
	data.append("file", file)
	if (ancestorId) data.append("ancestor_id", ancestorId)
	return httpx
		.upload<DocumentUploadResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}`, data, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function create(file: File, bid: string, ...options: httpx.option[]): CancellablePromise<DocumentUploadResponse> {
	return upload(file, bid, undefined, ...options)
}

export function revise(file: File, bid: string, ancestorId: string, ...options: httpx.option[]): CancellablePromise<DocumentUploadResponse> {
	return upload(file, bid, ancestorId, ...options)
}

export namespace searches {
	export function zero(sr: Partial<DocumentSearchRequest> = {}): DocumentSearchRequest {
		return {
			offset: 0n,
			limit: 20n,
			uploaded_by: [],
			deprecated_uploaded_by: "",
			brand_id: uuid.NIL,
			queues: [],
			approved: [],
			observation: [],
			confidence: { min: 0, max: 1 },
			...sr,
		}
	}
}

export function search(
	bid: string,
	req: DocumentSearchRequest,
	...options: httpx.option[]
): CancellablePromise<DocumentSearchResponse> {
	return httpx.get<DocumentSearchResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}`, req, ...options).then((resp) => ({
		...resp,
		items: (resp.items || []).map((item) => ({ media: item.media, event: events.zero(item.event), pages: item.pages || [] })),
		next: searches.zero(resp.next),
	}))
}

export function find(id: string, bid: string, ...options: httpx.option[]): CancellablePromise<DocumentFindResponse> {
	return httpx
		.get<DocumentFindResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function patch(
	bid: string,
	id: string,
	req: DocumentPatchRequest,
	...options: httpx.option[]
): CancellablePromise<DocumentPatchResponse> {
	return httpx
		.patch<DocumentPatchResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}`, req, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function requeue(id: string, bid: string, ...options: httpx.option[]): Promise<DocumentFindResponse> {
	return httpx
		.post<DocumentFindResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}/requeue`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function review(id: string, bid: string, ...options: httpx.option[]): Promise<DocumentPatchResponse> {
	return httpx
		.post<DocumentPatchResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}/review`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function publish(id: string, bid: string, ...options: httpx.option[]): Promise<DocumentPatchResponse> {
	return httpx
		.post<DocumentPatchResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}/publish`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function destroy(bid: string, id: string, ...options: httpx.option[]): Promise<DocumentPatchResponse> {
	return httpx
		.destroy<DocumentPatchResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
			pages: resp.pages || [],
		}))
}

export function versions(id: string, bid: string, ...options: httpx.option[]): CancellablePromise<DocumentVersionHistoryResponse> {
	return httpx
		.get<DocumentVersionHistoryResponse>(`${httpx.urlstorage.host()}/bg/document/${bid}/${id}/versions`, {}, ...options)
		.then((resp) => ({
			versions: (resp.versions || []).map((item) => ({ media: item.media, event: events.zero(item.event), pages: item.pages || [] })),
		}))
}
